import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ArrowLeft, Upload, FileSpreadsheet, Check, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_OPTIONS = [
  { value: "-", label: "—" },
  { value: "Keeps", label: "Keept" },
  { value: "KeeperBuddy", label: "K-Buddy" },
  { value: "Drives", label: "Rijdt" },
  { value: "Referees", label: "Fluit" },
  { value: "Coaches", label: "Coacht" },
];

const TABS = [
  { key: "players", label: "Spelers" },
  { key: "matches", label: "Wedstrijden" },
  { key: "activities", label: "Activiteiten" },
  { key: "planning", label: "Planning" },
];

export const ImportExcel = () => {
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [seasonPartId, setSeasonPartId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState("players");
  const [visitedTabs, setVisitedTabs] = useState({ players: true });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadSeason = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/seasons`);
      const s = (res.data || [])[0];
      setSeason(s || null);
      const parts = s?.season_parts || [];
      if (parts.length > 0) setSeasonPartId(parts[0].id);
    } catch {
      // ignore — user might not have a season yet
    }
  }, []);

  useEffect(() => { loadSeason(); }, [loadSeason]);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Kies eerst een Excel-bestand");
      return;
    }
    if (!season) {
      toast.error("Maak eerst een seizoen aan onder 'Aan de slag'");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      // Start async job — LLM call can outrun the Cloudflare proxy timeout (~100s),
      // so the backend returns a job_id and we poll for the result.
      const startRes = await axios.post(`${API}/import/excel-preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error("Geen job-id ontvangen van server");

      // Poll: max ~5 minutes at 2s interval (LLM can take up to ~3 min on large sheets)
      let attempts = 0;
      const maxAttempts = 150;
      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2000));
        attempts += 1;
        const pollRes = await axios.get(`${API}/import/excel-preview/${jobId}`, { timeout: 30000 });
        const { status, preview: previewData, detail } = pollRes.data;
        if (status === "done") {
          setPreview(previewData);
          toast.success("Sheet geanalyseerd — controleer de preview");
          return;
        }
        if (status === "error") {
          toast.error(`Import mislukt: ${detail || "onbekende fout"}`);
          return;
        }
      }
      toast.error("Import duurde te lang — probeer het met een kleinere sheet.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Import mislukt: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || !season || !seasonPartId) return;
    setConfirmOpen(false);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/import/confirm`, {
        season_id: season.id,
        season_part_id: seasonPartId,
        preview,
      });
      toast.success(
        `Klaar! ${res.data.players_created} spelers, ${res.data.matches_created} wedstrijden, ${res.data.activities_created} activiteiten geïmporteerd.`
      );
      navigate("/overview");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Bevestigen mislukt: ${detail}`);
    } finally {
      setImporting(false);
    }
  };

  const openConfirm = () => {
    setConfirmOpen(true);
  };

  const updatePlayer = (i, patch) => {
    setPreview((p) => ({ ...p, players: p.players.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  };
  const removePlayer = (i) => {
    setPreview((p) => ({ ...p, players: p.players.filter((_, idx) => idx !== i), assignments: p.assignments.filter((a) => a.player_index !== i).map((a) => ({ ...a, player_index: a.player_index > i ? a.player_index - 1 : a.player_index })) }));
  };
  const updateMatch = (i, patch) => {
    setPreview((p) => ({ ...p, matches: p.matches.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  };
  const removeMatch = (i) => {
    setPreview((p) => ({ ...p, matches: p.matches.filter((_, idx) => idx !== i), assignments: p.assignments.filter((a) => a.match_index !== i).map((a) => ({ ...a, match_index: a.match_index > i ? a.match_index - 1 : a.match_index })) }));
  };
  const updateActivity = (i, patch) => {
    setPreview((p) => ({ ...p, activities: p.activities.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  };
  const removeActivity = (i) => {
    setPreview((p) => ({ ...p, activities: p.activities.filter((_, idx) => idx !== i) }));
  };
  const setAssignment = (matchIdx, playerIdx, tasks) => {
    // tasks is an array of task strings (multi-task supported).
    setPreview((p) => {
      const others = (p.assignments || []).filter(
        (a) => !(a.match_index === matchIdx && a.player_index === playerIdx)
      );
      const next = [
        ...others,
        ...tasks.filter((t) => t && t !== "-").map((task) => ({ match_index: matchIdx, player_index: playerIdx, task })),
      ];
      return { ...p, assignments: next };
    });
  };
  const getAssignments = (matchIdx, playerIdx) => {
    return (preview?.assignments || [])
      .filter((a) => a.match_index === matchIdx && a.player_index === playerIdx)
      .map((a) => a.task);
  };
  const setActivityAssignment = (activityIdx, playerIdx, on) => {
    setPreview((p) => {
      const others = (p.activity_assignments || []).filter(
        (a) => !(a.activity_index === activityIdx && a.player_index === playerIdx)
      );
      const next = on ? [...others, { activity_index: activityIdx, player_index: playerIdx }] : others;
      return { ...p, activity_assignments: next };
    });
  };
  const getActivityAssignment = (activityIdx, playerIdx) => {
    return !!(preview?.activity_assignments || []).find((a) => a.activity_index === activityIdx && a.player_index === playerIdx);
  };

  // ---------- Render ----------
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="import-excel-page">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <button className="hover:text-slate-900 flex items-center gap-1" onClick={() => navigate("/get-started")} data-testid="back-to-onboarding">
          <ArrowLeft className="w-4 h-4" /> Aan de slag
        </button>
      </div>

      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900" style={{ fontFamily: "var(--font-display)" }}>
          Importeer Excel
        </h1>
        <p className="text-slate-500 mt-2">
          Upload je bestaande Excel-planning. Sportly leest de sheet uit en zet 'm klaar als preview — jij bevestigt of corrigeert voor definitief importeren.
        </p>
      </div>

      {!preview && (
        <Card className="border-dashed border-2 border-slate-300 bg-slate-50/50">
          <CardContent className="p-8 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Kies een .xlsx-bestand</p>
                <p className="text-xs text-slate-500">Max 5 MB, eerste werkblad wordt geanalyseerd</p>
              </div>
            </div>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer hover:file:bg-slate-800"
              data-testid="import-file-input"
            />
            {file && (
              <p className="text-xs text-slate-600">Geselecteerd: <span className="font-medium">{file.name}</span> ({(file.size / 1024).toFixed(0)} KB)</p>
            )}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className={"bg-slate-900 hover:bg-slate-800 " + (!file || uploading ? "opacity-50 pointer-events-none" : "")}
              data-testid="import-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Analyseren…" : file ? "Uploaden en analyseren" : "Kies eerst een bestand"}
            </Button>
            {!season && (
              <p className="text-xs text-amber-600">Je moet eerst een seizoen aanmaken voordat je kunt importeren.</p>
            )}
          </CardContent>
        </Card>
      )}

      {preview && (
        <>
          {preview.notes && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900" data-testid="import-notes">
              <span className="font-semibold">Opmerkingen: </span>{preview.notes}
            </div>
          )}

          {season?.season_parts?.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Importeer in seizoensdeel:</span>
              <Select value={seasonPartId} onValueChange={setSeasonPartId}>
                <SelectTrigger className="w-56 h-8" data-testid="import-part-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {season.season_parts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-slate-200">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setVisitedTabs((v) => ({ ...v, [t.key]: true })); }}
                className={"px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors " + (tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700")}
                data-testid={`import-tab-${t.key}`}
              >
                {visitedTabs[t.key] ? (
                  <Check className="w-3 h-3 mr-1 inline text-emerald-500" />
                ) : (
                  <span className="w-3 h-3 mr-1 inline-block" />
                )}
                {t.label} ({
                  t.key === "players" ? preview.players.length :
                  t.key === "matches" ? preview.matches.length :
                  t.key === "activities" ? preview.activities.length :
                  preview.assignments.length + (preview.activity_assignments || []).length
                })
              </button>
            ))}
          </div>

          {tab === "players" && <PlayersTable rows={preview.players} onChange={updatePlayer} onRemove={removePlayer} />}
          {tab === "matches" && <MatchesTable rows={preview.matches} onChange={updateMatch} onRemove={removeMatch} />}
          {tab === "activities" && <ActivitiesTable rows={preview.activities} onChange={updateActivity} onRemove={removeActivity} />}
          {tab === "planning" && (
            <PlanningTable
              matches={preview.matches}
              activities={preview.activities}
              players={preview.players}
              getTasks={getAssignments}
              onSet={setAssignment}
              getActivity={getActivityAssignment}
              onSetActivity={setActivityAssignment}
            />
          )}

          <div className="flex items-center justify-between border-t border-slate-200 pt-4">
            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }} data-testid="import-reset-btn">
              Opnieuw beginnen
            </Button>
            <Button onClick={openConfirm} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-confirm-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {importing ? "Importeren…" : "Bevestig import"}
            </Button>
          </div>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent data-testid="import-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Heb je alle tabs gecontroleerd?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {(() => {
                    const missing = TABS.filter((t) => !visitedTabs[t.key]).map((t) => t.label);
                    return missing.length > 0
                      ? `Je hebt deze tabs nog niet geopend: ${missing.join(", ")}. Controleer ze eerst — na bevestigen kun je de preview niet meer aanpassen.`
                      : "Je hebt alle 4 tabs geopend. Weet je zeker dat je wilt importeren? Deze actie kan niet ongedaan worden gemaakt.";
                  })()}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="import-confirm-cancel">Terug naar preview</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-confirm-yes">
                  Ja, importeer nu
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
};

// ---------- Table components ----------
const cellCls = "px-2 py-1.5 border-b border-slate-100";

const PlayersTable = ({ rows, onChange, onRemove }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-md">
    <table className="w-full text-sm" data-testid="import-players-table">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
        <tr>
          <th className="text-left px-2 py-2">Voornaam</th>
          <th className="text-left px-2 py-2">Achternaam</th>
          <th className="text-center px-2 py-2">Keeper</th>
          <th className="text-center px-2 py-2">Fluit</th>
          <th className="text-center px-2 py-2">Kan rijden</th>
          <th className="w-10"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={i} className="hover:bg-slate-50">
            <td className={cellCls}><Input value={p.first_name || ""} onChange={(e) => onChange(i, { first_name: e.target.value })} className="h-7 text-sm" /></td>
            <td className={cellCls}><Input value={p.last_name || ""} onChange={(e) => onChange(i, { last_name: e.target.value })} className="h-7 text-sm" /></td>
            <td className={cellCls + " text-center"}><Checkbox checked={!!p.is_goalkeeper} onCheckedChange={(v) => onChange(i, { is_goalkeeper: !!v })} /></td>
            <td className={cellCls + " text-center"}><Checkbox checked={!!p.is_referee} onCheckedChange={(v) => onChange(i, { is_referee: !!v })} /></td>
            <td className={cellCls + " text-center"}><Checkbox checked={p.can_drive !== false} onCheckedChange={(v) => onChange(i, { can_drive: !!v })} /></td>
            <td className={cellCls + " text-center"}>
              <button onClick={() => onRemove(i)} className="text-rose-500 hover:text-rose-700" data-testid={`import-player-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">Geen spelers gedetecteerd</td></tr>}
      </tbody>
    </table>
  </div>
);

const MatchesTable = ({ rows, onChange, onRemove }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-md">
    <table className="w-full text-sm" data-testid="import-matches-table">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
        <tr>
          <th className="text-left px-2 py-2">Datum</th>
          <th className="text-left px-2 py-2">T/U</th>
          <th className="text-left px-2 py-2">Tegenstander (club)</th>
          <th className="text-left px-2 py-2">Team</th>
          <th className="text-left px-2 py-2">Verzamelen</th>
          <th className="text-left px-2 py-2">Aanvang</th>
          <th className="w-10"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((m, i) => (
          <tr key={i} className="hover:bg-slate-50">
            <td className={cellCls}><Input type="date" value={m.date || ""} onChange={(e) => onChange(i, { date: e.target.value })} className="h-7 text-sm" /></td>
            <td className={cellCls}>
              <Select value={m.is_home ? "home" : "away"} onValueChange={(v) => onChange(i, { is_home: v === "home", shirt: v === "home" ? "Home" : "Away" })}>
                <SelectTrigger className="h-7 w-20 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="home">Thuis</SelectItem><SelectItem value="away">Uit</SelectItem></SelectContent>
              </Select>
            </td>
            <td className={cellCls}><Input value={m.opponent_club || ""} onChange={(e) => onChange(i, { opponent_club: e.target.value })} className="h-7 text-sm" /></td>
            <td className={cellCls}><Input value={m.opponent_team || ""} onChange={(e) => onChange(i, { opponent_team: e.target.value })} className="h-7 text-sm w-24" /></td>
            <td className={cellCls}><Input value={m.gather_time || ""} onChange={(e) => onChange(i, { gather_time: e.target.value })} placeholder="HH:MM" className="h-7 text-sm w-20" /></td>
            <td className={cellCls}><Input value={m.start_time || ""} onChange={(e) => onChange(i, { start_time: e.target.value })} placeholder="HH:MM" className="h-7 text-sm w-20" /></td>
            <td className={cellCls + " text-center"}>
              <button onClick={() => onRemove(i)} className="text-rose-500 hover:text-rose-700" data-testid={`import-match-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-6">Geen wedstrijden gedetecteerd</td></tr>}
      </tbody>
    </table>
  </div>
);

const ActivitiesTable = ({ rows, onChange, onRemove }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-md">
    <table className="w-full text-sm" data-testid="import-activities-table">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
        <tr>
          <th className="text-left px-2 py-2">Datum</th>
          <th className="text-left px-2 py-2">Omschrijving</th>
          <th className="text-left px-2 py-2">Tijd</th>
          <th className="w-10"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a, i) => (
          <tr key={i} className="hover:bg-slate-50">
            <td className={cellCls}><Input type="date" value={a.date || ""} onChange={(e) => onChange(i, { date: e.target.value })} className="h-7 text-sm" /></td>
            <td className={cellCls}><Input value={a.description || ""} onChange={(e) => onChange(i, { description: e.target.value })} className="h-7 text-sm" /></td>
            <td className={cellCls}><Input value={a.time || ""} onChange={(e) => onChange(i, { time: e.target.value })} placeholder="HH:MM" className="h-7 text-sm w-20" /></td>
            <td className={cellCls + " text-center"}>
              <button onClick={() => onRemove(i)} className="text-rose-500 hover:text-rose-700" data-testid={`import-activity-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-6">Geen activiteiten gedetecteerd</td></tr>}
      </tbody>
    </table>
  </div>
);

const TASK_SHORTS = { Keeps: "K", KeeperBuddy: "KB", Drives: "R", Referees: "F", Coaches: "C" };

const MultiTaskCell = ({ selected, onChange, testid }) => {
  const label = selected.length === 0
    ? "—"
    : selected.map((t) => TASK_SHORTS[t] || t).join("+");
  const toggle = (task) => {
    if (selected.includes(task)) onChange(selected.filter((t) => t !== task));
    else onChange([...selected, task]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            "h-7 w-full rounded border text-xs font-semibold " +
            (selected.length > 0
              ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
              : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50")
          }
          data-testid={testid}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-2" align="center">
        <div className="space-y-1">
          {TASK_OPTIONS.filter((o) => o.value !== "-").map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className={
                "w-full text-left px-2 py-1 rounded text-sm flex items-center gap-2 " +
                (selected.includes(o.value) ? "bg-slate-100" : "hover:bg-slate-50")
              }
              data-testid={`${testid}-opt-${o.value}`}
            >
              <span className={"w-4 h-4 rounded-sm border flex items-center justify-center " + (selected.includes(o.value) ? "bg-slate-900 border-slate-900" : "border-slate-300")}>
                {selected.includes(o.value) && <Check className="w-3 h-3 text-white" />}
              </span>
              <span>{o.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PlanningTable = ({ matches, activities, players, getTasks, onSet, getActivity, onSetActivity }) => {
  if ((matches.length === 0 && activities.length === 0) || players.length === 0) {
    return <div className="border border-slate-200 rounded-md p-6 text-center text-sm text-slate-400">Planning vereist minimaal één wedstrijd of activiteit en één speler</div>;
  }
  return (
    <div className="overflow-auto border border-slate-200 rounded-md max-h-[70vh]" data-testid="import-planning-table">
      <table className="text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
          <tr>
            <th className="text-left px-2 py-2 sticky left-0 bg-slate-50 z-20 min-w-[180px]">Regel</th>
            {players.map((p, i) => (
              <th key={i} className="text-center px-2 py-2 min-w-[80px]" title={`${p.first_name || ""} ${p.last_name || ""}`.trim()}>
                {(p.last_name || p.first_name || "").substring(0, 8)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matches.map((m, mi) => (
            <tr key={`m-${mi}`} className="hover:bg-slate-50">
              <td className="px-2 py-1.5 border-b border-slate-100 sticky left-0 bg-white z-10 whitespace-nowrap">
                <span className="text-xs text-slate-500">{m.date}</span>
                <span className="mx-1 text-slate-300">·</span>
                <span className="text-xs">{m.is_home ? "Thuis" : "Uit"}</span>
                <span className="mx-1 text-slate-300">·</span>
                <span className="text-xs font-medium">{m.opponent_club || "—"}</span>
              </td>
              {players.map((p, pi) => (
                <td key={pi} className="px-1 py-1 border-b border-slate-100 text-center">
                  <MultiTaskCell
                    selected={getTasks(mi, pi)}
                    onChange={(tasks) => onSet(mi, pi, tasks)}
                    testid={`import-m-${mi}-p-${pi}`}
                  />
                </td>
              ))}
            </tr>
          ))}
          {activities.map((a, ai) => (
            <tr key={`a-${ai}`} className="hover:bg-indigo-50/40 bg-indigo-50/20">
              <td className="px-2 py-1.5 border-b border-slate-100 sticky left-0 bg-indigo-50/40 z-10 whitespace-nowrap">
                <span className="text-xs text-indigo-700 font-semibold uppercase mr-1">Activ.</span>
                <span className="text-xs text-slate-500">{a.date || "—"}</span>
                <span className="mx-1 text-slate-300">·</span>
                <span className="text-xs font-medium">{a.description}</span>
              </td>
              {players.map((p, pi) => (
                <td key={pi} className="px-1 py-1 border-b border-slate-100 text-center">
                  <button
                    type="button"
                    onClick={() => onSetActivity(ai, pi, !getActivity(ai, pi))}
                    className={
                      "h-7 w-full rounded text-xs font-semibold border transition-colors " +
                      (getActivity(ai, pi)
                        ? "bg-indigo-500 text-white border-indigo-500 hover:bg-indigo-600"
                        : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50")
                    }
                    data-testid={`import-act-${ai}-p-${pi}`}
                  >
                    {getActivity(ai, pi) ? "A" : "—"}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ImportExcel;
